/**
 * Error boundary for lazy-loaded route chunks (post-deploy stale hash recovery).
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { isChunkLoadError, reloadOnceForStaleChunks } from '../../utils/lazyWithRetry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class LazyRouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[LazyRouteErrorBoundary]', error, errorInfo);
    if (isChunkLoadError(error)) {
      try {
        reloadOnceForStaleChunks();
      } catch {
        // reloadOnceForStaleChunks throws when a second reload is blocked
      }
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const chunkError = isChunkLoadError(this.state.error);
    const message =
      this.state.error?.message ||
      'This page could not be loaded. The app may have been updated — try refreshing.';

    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" aria-hidden />
        <h2 className="text-xl font-semibold text-theme-text mb-2">
          {chunkError ? 'Page update required' : 'Something went wrong'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mb-4">{message}</p>
        {chunkError && (
          <p className="text-xs text-gray-500 dark:text-gray-500 max-w-md mb-4">
            If the problem continues, hard-refresh with Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac).
          </p>
        )}
        <Button type="button" onClick={this.handleReload}>
          Reload page
        </Button>
      </div>
    );
  }
}
